// =============================================================================
// customerExcel.js
// Excel template export / import for customer master data:
//   * Customer groups  (md_customer_groups)
//   * Customers        (md_customers)
//
// Flat layout — one row per record. The customer sheet's "Customer Group" and
// both sheets' "Active" columns carry in-cell dropdowns (data validation)
// backed by a hidden "Lists" sheet, the same way the volume templates work.
//
// Import returns { rows, errors }; `rows` is empty when there are any errors so
// the caller can refuse a partial import.
// =============================================================================
import ExcelJS from 'exceljs';

const DELIM = ' — ';       // separates "CODE — Description" in dropdown values
const NONE = '(None)';     // explicit "no group" choice
const BLANK_ROWS = 200;    // empty rows (with dropdowns) appended after the data
const ACTIVE_OPTS = ['Active', 'Inactive'];

// "G1 — Major Retail" -> "G1"; plain "G1" -> "G1".
const codeOf = (raw) => {
  if (raw == null) return '';
  const s = String(raw).trim();
  const i = s.indexOf(DELIM);
  return (i >= 0 ? s.slice(0, i) : s).trim();
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// ExcelJS cell.value may be a string, number, or rich-text/formula object.
const cellText = (cell) => {
  const v = cell?.value;
  if (v == null) return '';
  if (typeof v === 'object') return String(v.text ?? v.result ?? '');
  return String(v);
};

const boolToLabel = (v) => (v === false ? 'Inactive' : 'Active');
const labelToBool = (raw) => String(raw).trim().toLowerCase() !== 'inactive';

// Describe each master-data kind: sheet name and its columns. A column may be
// { key, header, required } and optionally { list: 'groups' } for a dropdown or
// { active: true } for the Active toggle column.
export function makeCustomerSpec(kind) {
  if (kind === 'customers') {
    return {
      kind: 'customers',
      sheet: 'Customers',
      fileLabel: 'Customers',
      keyField: 'customer_code',
      columns: [
        { key: 'customer_code', header: 'Customer Code', required: true },
        { key: 'customer_name', header: 'Customer Name', required: true },
        { key: 'customer_group_code', header: 'Customer Group', list: 'groups' },
        { key: 'is_active', header: 'Active', active: true },
      ],
    };
  }
  return {
    kind: 'groups',
    sheet: 'Customer Groups',
    fileLabel: 'Customer_Groups',
    keyField: 'customer_group_code',
    columns: [
      { key: 'customer_group_code', header: 'Group Code', required: true },
      { key: 'group_name', header: 'Group Name', required: true },
      { key: 'is_active', header: 'Active', active: true },
    ],
  };
}

// Group dropdown values: "code — name".
const groupOptions = (groups = []) => groups.map((g) => `${g.customer_group_code}${DELIM}${g.group_name ?? ''}`);
const groupLabelMap = (groups = []) =>
  Object.fromEntries((groups || []).map((g) => [g.customer_group_code, `${g.customer_group_code}${DELIM}${g.group_name ?? ''}`]));

// ---- Export -----------------------------------------------------------------
// `rows` are records straight from the grid load ({ customer_code, ... }).
// `groups` is the customer-groups list (for the customer sheet's dropdown).
export async function exportCustomerTemplate({ kind, rows = [], groups = [], blankRows = BLANK_ROWS }) {
  const spec = makeCustomerSpec(kind);
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Mantle';
  wb.created = new Date();

  const ws = wb.addWorksheet(spec.sheet, { views: [{ state: 'frozen', ySplit: 1 }] });
  const lists = wb.addWorksheet('Lists');
  lists.state = 'veryHidden';

  // Hidden lists: column A = groups (with a leading "(None)"), column B = Active.
  const groupOpts = [NONE, ...groupOptions(groups)];
  lists.getCell('A1').value = 'Customer Group';
  groupOpts.forEach((v, i) => { lists.getCell(`A${i + 2}`).value = v; });
  lists.getCell('B1').value = 'Active';
  ACTIVE_OPTS.forEach((v, i) => { lists.getCell(`B${i + 2}`).value = v; });

  ws.columns = spec.columns.map((col) => ({
    header: col.header,
    key: col.key,
    width: col.key.endsWith('_name') ? 34 : col.active ? 12 : 26,
  }));

  const headerRow = ws.getRow(1);
  headerRow.font = { bold: true, color: { argb: 'FFF8FAFC' } };
  headerRow.alignment = { vertical: 'middle' };
  headerRow.height = 20;
  headerRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000000' } };
  });

  const grpLabels = groupLabelMap(groups);
  rows.forEach((r, i) => {
    const row = ws.getRow(i + 2);
    spec.columns.forEach((col, ci) => {
      const cell = row.getCell(ci + 1);
      if (col.active) cell.value = boolToLabel(r[col.key]);
      else if (col.list === 'groups') cell.value = r[col.key] ? (grpLabels[r[col.key]] || r[col.key]) : NONE;
      else cell.value = r[col.key] ?? '';
    });
  });

  // Data validation (dropdowns) for the group + active columns.
  const lastRow = rows.length + blankRows + 1;
  const groupColIdx = spec.columns.findIndex((c) => c.list === 'groups');
  const activeColIdx = spec.columns.findIndex((c) => c.active);
  const colLetter = (idx) => String.fromCharCode(65 + idx); // 0 -> A
  for (let r = 2; r <= lastRow; r++) {
    if (groupColIdx >= 0) {
      ws.getCell(`${colLetter(groupColIdx)}${r}`).dataValidation = {
        type: 'list', allowBlank: true,
        formulae: [`=Lists!$A$2:$A$${groupOpts.length + 1}`],
        showErrorMessage: true, errorStyle: 'warning',
        error: 'Pick a customer group from the list.', errorTitle: 'Pick from the list',
      };
    }
    if (activeColIdx >= 0) {
      ws.getCell(`${colLetter(activeColIdx)}${r}`).dataValidation = {
        type: 'list', allowBlank: true,
        formulae: [`=Lists!$B$2:$B$${ACTIVE_OPTS.length + 1}`],
        showErrorMessage: true, errorStyle: 'warning',
        error: 'Choose Active or Inactive.', errorTitle: 'Pick from the list',
      };
    }
  }

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, `${spec.fileLabel}_Template.xlsx`);
}

// ---- Import -----------------------------------------------------------------
// Returns { rows, errors }. Each returned row matches the upsert payload shape.
// `groups` (customer-groups list) is used to validate the group dimension.
export async function parseCustomerTemplate(file, kind, groups = []) {
  const spec = makeCustomerSpec(kind);
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const ws = wb.getWorksheet(spec.sheet) || wb.worksheets[0];
  if (!ws) return { rows: [], errors: ['Could not find a worksheet in the file.'] };

  const groupSet = new Set((groups || []).map((g) => g.customer_group_code));

  const out = [];
  const errors = [];
  const seen = new Set();

  ws.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // header

    const raw = spec.columns.map((_, ci) => cellText(row.getCell(ci + 1)));
    // Skip entirely blank rows.
    if (!raw.some((s) => String(s).trim())) return;

    const rowErrs = [];
    const rec = {};
    spec.columns.forEach((col, ci) => {
      const text = String(raw[ci]).trim();
      if (col.active) {
        rec[col.key] = labelToBool(text || 'Active');
      } else if (col.list === 'groups') {
        if (!text || text === NONE) { rec[col.key] = null; return; }
        const code = codeOf(text);
        if (!groupSet.has(code)) rowErrs.push(`unknown Customer Group "${code}"`);
        rec[col.key] = code;
      } else {
        if (!text && col.required) rowErrs.push(`missing ${col.header}`);
        rec[col.key] = text || null;
      }
    });

    const keyVal = rec[spec.keyField];
    if (keyVal) {
      if (seen.has(keyVal)) rowErrs.push(`duplicate ${spec.keyField} "${keyVal}" in file`);
      else seen.add(keyVal);
    }

    if (rowErrs.length) { errors.push(`Row ${rowNumber}: ${rowErrs.join(', ')}`); return; }
    out.push(rec);
  });

  if (errors.length) return { rows: [], errors };
  return { rows: out, errors: [] };
}

export default { makeCustomerSpec, exportCustomerTemplate, parseCustomerTemplate };
